/**
 * The one sum-preserving split (ADR 060 — Money Type § 2.4): largest remainder
 * over whole minor units. No rounding mode guarantees a sum; this does.
 */
import Decimal from 'decimal.js';

/**
 * Splits a whole number of units across weights so the parts sum to exactly
 * `units`. Each part gets the floor of its exact share; the leftover units go
 * one each to the parts with the largest fractional remainder, ties to the
 * earliest index — so the result is deterministic for a given input order.
 *
 * @param units   - A non-negative integer count of minor units.
 * @param weights - Non-negative finite weights with a positive sum.
 * @returns One integer `Decimal` per weight, summing to `units`.
 * @throws RangeError when `units` is not a non-negative integer, or the weights are
 *   empty, negative, non-finite, or sum to zero.
 */
export function largestRemainder(units: Decimal, weights: readonly Decimal[]): Decimal[] {
    if (!units.isInteger() || units.isNegative()) {
        throw new RangeError(`largestRemainder: units must be a non-negative integer, got ${units.toString()}`);
    }
    assertWeights(weights);

    const total = weights.reduce((acc, w) => acc.plus(w), new Decimal(0));
    const exact = weights.map((w) => units.times(w).dividedBy(total));
    const parts = exact.map((share) => share.floor());
    const leftover = parts.reduce((acc, p) => acc.minus(p), units).toNumber();

    // Stable sort keeps ties in index order, so the earliest part wins a tie.
    const byRemainder = exact
        .map((share, index) => ({ index, remainder: share.minus(share.floor()) }))
        .sort((a, b) => b.remainder.comparedTo(a.remainder));
    // Floors can only undershoot, by fewer units than there are parts. Anything
    // else means Decimal precision rounded a share up; fail loud, never mis-split.
    if (leftover < 0 || leftover > weights.length) {
        throw new RangeError(`largestRemainder: ${units.toString()} units cannot be split exactly across these weights`);
    }
    for (let i = 0; i < leftover; i += 1) {
        const { index } = byRemainder[i];
        parts[index] = parts[index].plus(1);
    }
    return parts;
}

function assertWeights(weights: readonly Decimal[]): void {
    if (weights.length === 0) {
        throw new RangeError('largestRemainder: at least one weight is required');
    }
    for (const w of weights) {
        if (!w.isFinite() || w.isNegative()) {
            throw new RangeError(`largestRemainder: weights must be finite and non-negative, got ${w.toString()}`);
        }
    }
    if (weights.every((w) => w.isZero())) {
        throw new RangeError('largestRemainder: weights must not all be zero');
    }
}
